import { defineStore } from 'pinia';
import { fetchTimesheetSetup } from '@/lib/request/fetchTimesheetSetup';
import {
  CustomField,
  CustomFieldData,
  CustomFieldType,
  CustomFieldTypes,
  CustomFieldTypeValues,
  TimesheetSetup,
} from '@/lib/request/types/timesheetSetup';
import { useTimesheetStore } from './timesheets';
import { GetTimesheetSetupRequestPayload } from '@/lib/request/types/kiosk';
import { useRosterStore } from '@/stores/rosters';
import { DateTime } from 'luxon';

export interface TimesheetSetupStoreMetadata {
  areaId: number;
}

export interface TimesheetSetupStoreStatus {
  isLoading: boolean;
}

export interface TimesheetSetupStoreErrors {
  loadingError: Error | null;
}

export interface TimesheetSetupStore {
  data: TimesheetSetup;
  metadata: TimesheetSetupStoreMetadata;
  status: TimesheetSetupStoreStatus;
  errors: TimesheetSetupStoreErrors;
}

export const useTimesheetSetupStore = defineStore({
  id: 'timesheetSetup',
  state: (): TimesheetSetupStore => ({
    data: {} as TimesheetSetup,
    metadata: {
      areaId: 0,
    },
    status: {
      isLoading: false,
    },
    errors: {
      loadingError: null,
    },
  }),
  getters: {
    isLoading: (state) => {
      return state.status.isLoading;
    },
    areaId(state): number {
      return state.metadata.areaId;
    },
    getAllCustomFields(state): () => CustomField[] {
      return () =>
        state.data.CustomField?.filter((customField) => {
          return (
            this.convertCustomFieldTypeIntToString(customField.Type) !==
            CustomFieldType.File
          );
        });
    },
    getCustomFieldData(state): () => CustomFieldData {
      return () => state.data.CustomFieldData;
    },
    convertCustomFieldTypeIntToString(
      state
    ): (type: number) => CustomFieldTypeValues {
      return (type: number) =>
        state.data.CustomFieldType[type.toString() as keyof CustomFieldTypes];
    },
    isBreakDurationLongerThanMinimum() {
      const timesheets = useTimesheetStore();

      return (
        DateTime.now().toSeconds() -
          timesheets.getActiveBreak()!.intUnixStart >=
        timesheets.getActiveBreak()?.intUnixEnd -
          timesheets.getActiveBreak()!.intUnixStart
      );
    },
    isTimeToShiftWithinTimeframe(state) {
      const rosters = useRosterStore();
      const SECONDS_IN_MINUTE = 60;

      return (
        Math.ceil(
          (rosters.getStartRosterTime()! - DateTime.now().toSeconds()) /
            SECONDS_IN_MINUTE
        ) <= +state.data.LocationSettings?.CAN_CLOCKIN_SHIFT_EARLIER_MINS
      );
    },
    isClockInUnavailable(state) {
      return () =>
        !state.data.LocationSettings?.CAN_CLOCKIN_SHIFT_EARLIER &&
        !this.isTimeToShiftWithinTimeframe;
    },
    isEndBreakUnavailable(state) {
      return () =>
        !state.data.LocationSettings?.CAN_END_BREAK_EARLIER &&
        !this.isBreakDurationLongerThanMinimum;
    },
    canModifyTimesheetOnEnd(state) {
      return !!state.data.LocationSettings?.CAN_MODIFY_TIMESHEET_ON_END;
    },
    canAddEditTimesheet(state) {
      return !!state.data.LocationSettings?.CAN_SUBMIT_SHIFT_VIA_DESK;
    },
  },
  actions: {
    async load(displayTiming: number, areaId: number) {
      const payload: GetTimesheetSetupRequestPayload = {
        displayTiming,
        timesheetId: useTimesheetStore().activeTimesheet?.Id || undefined,
        departmentId: areaId,
      };

      this.errors.loadingError = null;
      this.status.isLoading = true;
      try {
        this.data = await fetchTimesheetSetup(payload);
        this.metadata.areaId = areaId;
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },
  },
});
